import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { gradients } from '../../theme/patterns';

export interface LoadingSpinnerProps {
  size?: number | 'small' | 'medium' | 'large';
  message?: string;
  variant?: 'circular' | 'linear' | 'dots' | 'pulse';
  fullScreen?: boolean;
  overlay?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  message,
  variant = 'circular',
  fullScreen = false,
  overlay = false,
}) => {
  const getSize = () => {
    if (typeof size === 'number')
      return size;
    switch (size) {
      case 'small':
        return 24;
      case 'medium':
        return 40;
      case 'large':
        return 60;
      default:
        return 40;
    }
  };

  const pixelSize = getSize();

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    ...(fullScreen && {
      position: 'fixed',
      top: 0,
      left: 'var(--sidebar-width, 0px)',
      right: 0,
      bottom: 0,
      zIndex: 9999,
      backgroundColor: overlay ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
      backdropFilter: overlay ? 'blur(8px)' : 'none',
      pointerEvents: 'none',
    }),
    ...(!fullScreen
      && overlay && {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(8px)',
      pointerEvents: 'none',
    }),
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'circular':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <CircularProgress
              size={pixelSize}
              thickness={4}
              sx={{
                'color': 'primary.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
          </motion.div>
        );

      case 'linear':
        return (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '100%' }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', maxWidth: 300 }}
          >
            <LinearProgress
              sx={{
                'height': 6,
                'borderRadius': 3,
                'backgroundColor': 'rgba(59, 111, 217, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: gradients.primary,
                },
              }}
            />
          </motion.div>
        );

      case 'dots':
        return (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[0, 1, 2].map(index => (
              <motion.div
                key={index}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.2,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: index * 0.2,
                  ease: 'easeInOut',
                }}
              >
                <Box
                  sx={{
                    width: pixelSize / 4,
                    height: pixelSize / 4,
                    borderRadius: '50%',
                    background: gradients.primary,
                  }}
                />
              </motion.div>
            ))}
          </Box>
        );

      case 'pulse':
        return (
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 1.5,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          >
            <Box
              sx={{
                width: pixelSize,
                height: pixelSize,
                borderRadius: '50%',
                background: gradients.primary,
                boxShadow: '0 4px 20px rgba(59, 111, 217, 0.4)',
              }}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={containerSx}>
      {renderSpinner()}

      {message && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {message}
          </Typography>
        </motion.div>
      )}
    </Box>
  );
};

/**
 * Hook for managing loading states
 */
export function useLoading(initialState = false) {
  const [isLoading, setIsLoading] = React.useState(initialState);

  const startLoading = React.useCallback(() => setIsLoading(true), []);
  const stopLoading = React.useCallback(() => setIsLoading(false), []);
  const toggleLoading = React.useCallback(
    () => setIsLoading(prev => !prev),
    [],
  );

  return {
    isLoading,
    startLoading,
    stopLoading,
    toggleLoading,
    setIsLoading,
  };
}
